import { useCallback, useEffect, useMemo, useState } from 'react'
import { apiFetch } from '../../lib/api-fetch'

// Paket D1 (03.10.2026): eine Firma kann mehrere Webshops haben (Grundsatz 10: ein Schema je
// Firma, die Ladenspalte trennt die Laeden). Was einem Laden gehoert (Kategorien, Laden-Artikel
// samt Varianten, Sammlungen, Blog, Einstellungen), verlangt die API mit dem Kopf
// `x-webshop-id`; Katalogliste, Bilder und Optionen bleiben firmenweit
// (packages/api/src/routes/website.ts, webShopAusKopf). Dieses Modul ist die eine Stelle im
// Bereich Web Sitesi, die die Laeden der Firma laedt und weiss, welcher gewaehlt ist. Der Name
// ist bewusst nicht shopId: so heissen im ShopPanel die Marktplatz-Laeden (features/pricing).

/** Wire-Spiegel von WebsiteShopDTO (packages/api/src/routes/website.ts, SoT dort). */
export interface WebShop {
  id: string
  slug: string
  name: string
  theme: string
}

/** Suchparameter der Liste und der Detailmaske: `magaza` nennt den Laden, der beim Oeffnen
 *  gilt. Ob die Id zur Firma gehoert, entscheidet erst der Abgleich mit GET /shops. */
export function magazaSuche(search: Record<string, unknown>): { magaza?: string } {
  const magaza = search.magaza
  return typeof magaza === 'string' && magaza.length > 0 && magaza.length < 64 ? { magaza } : {}
}

/** Koepfe fuer alles, was einem Laden gehoert. Firmenweite Aufrufe schicken nur `x-firm-id`. */
export function webShopHeaders(firmId: string, webShopId: string): Record<string, string> {
  return { 'x-firm-id': firmId, 'x-webshop-id': webShopId }
}

// Gemerkt wird je Firma der zuletzt gewaehlte Laden. localStorage kann fehlen oder werfen
// (privates Fenster, gesperrte Websitedaten); dann gilt der aelteste Laden, die Seite
// funktioniert trotzdem.
const merkSchluessel = (firmId: string) => `shoppanel-webshop:${firmId}`

function gemerkterLaden(firmId: string): string | null {
  try {
    return localStorage.getItem(merkSchluessel(firmId))
  } catch {
    return null
  }
}

function ladenMerken(firmId: string, webShopId: string): void {
  try {
    localStorage.setItem(merkSchluessel(firmId), webShopId)
  } catch {
    // Ohne Speicher gilt die Wahl fuer diese Seite, nur nicht ueber sie hinaus.
  }
}

/** Antwort von GET /api/website/shops, festgehalten mit der Firma, fuer die sie galt. */
interface Stand {
  firmId: string
  shops: WebShop[]
  error: boolean
}

const KEINE_LAEDEN: WebShop[] = []

/**
 * Die Laeden der aktiven Firma und der gewaehlte. Vorrang hat die Wahl auf dieser Seite, dann
 * der Suchparameter `magaza`, dann der gemerkte Laden der Firma, dann der aelteste (die API
 * liefert den aeltesten zuerst). Eine fremde oder veraltete Id wird schlicht nicht gefunden.
 *
 * `shop` bleibt null, solange geladen wird, wenn das Laden scheiterte (`error`) oder wenn die
 * Firma keinen Laden hat. Eine Firma ohne Webshop-Tabellen meldet die API als `{ shops: [] }`;
 * jede eingerichtete Firma hat mindestens einen Laden (Migration
 * 2026-10-03-webshops-laeden-je-firma legt ihn an). „Kein Laden" und „nicht eingerichtet" sind
 * darum derselbe Fall.
 */
export function useWebShop(firmId: string, magaza: string | undefined) {
  const [stand, setStand] = useState<Stand | null>(null)
  const [gewaehlt, setGewaehlt] = useState<string | null>(null)

  useEffect(() => {
    if (!firmId) return
    let veraltet = false
    apiFetch('/api/website/shops', { headers: { 'x-firm-id': firmId } })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((data) => {
        const shops = (data as { shops?: WebShop[] }).shops ?? []
        if (!veraltet) setStand({ firmId, shops, error: false })
      })
      .catch(() => {
        if (!veraltet) setStand({ firmId, shops: [], error: true })
      })
    return () => {
      veraltet = true
    }
  }, [firmId])

  // Nur der Stand der AKTIVEN Firma zaehlt: nach einem Firmenwechsel stuende bis zur Antwort
  // sonst ein Laden der alten Firma da, und die Panels fragten mit der neuen Firma nach ihm.
  const aktuell = stand?.firmId === firmId ? stand : null

  const shop = useMemo(() => {
    if (!aktuell) return null
    const gemerkt = gemerkterLaden(aktuell.firmId)
    return (
      aktuell.shops.find((s) => s.id === gewaehlt) ??
      aktuell.shops.find((s) => s.id === magaza) ??
      aktuell.shops.find((s) => s.id === gemerkt) ??
      aktuell.shops[0] ??
      null
    )
  }, [aktuell, gewaehlt, magaza])

  const setShopId = useCallback(
    (id: string) => {
      setGewaehlt(id)
      ladenMerken(firmId, id)
    },
    [firmId],
  )

  return {
    shops: aktuell?.shops ?? KEINE_LAEDEN,
    shop,
    setShopId,
    loading: aktuell === null,
    error: aktuell?.error ?? false,
  }
}
